import { useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button";
import Slab from "../../components/Slab/Slab";
import { useEffect, useRef, useState } from "react";
import AlertPopupModal from "../../components/Modal/AlertPopupModal/AlertPopupModal";
import { parseMessage } from "../../utils/parseTemplateMessage";
import { UpdateNotifications } from "../../services/notification.service";
import useAuthService from "../../auth/AuthService";
import NullPageComponent from "../../components/Tile/NullPageComponent";

export const AlertPage: React.FC<{
  notifications?: any;
}> = ({ notifications }) => {
  const { isAuthenticated, getToken } = useAuthService();
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [state, setState] = useState({
    isLoading: false,
    isList: true,
    openNotificationId: null as string | null, // Track the opened notification modal
    notifications: [] as any[],
    selectedNotification: null as any,
  });

  useEffect(() => {
    setState((prev) => {
      const updatedSelected = prev.selectedNotification
        ? notifications?.find((n: any) => n.id === prev.selectedNotification.id) || null
        : null;

      const isSameSelected =
        (!prev.selectedNotification && !updatedSelected) ||
        (prev.selectedNotification?.id === updatedSelected?.id);

      const isSameNotifications =
        JSON.stringify(prev.notifications) === JSON.stringify(notifications || []);

      // Only update state if something actually changed
      if (isSameSelected && isSameNotifications) {
        return prev; // no change
      }

      return {
        ...prev,
        notifications: notifications || [],
        selectedNotification: updatedSelected,
      };
    });
  }, [notifications]);

  useEffect(() => {
    const handleClickOutside = () => {
      setState((prev) => ({ ...prev, openNotificationId: null }))
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [state.openNotificationId]);

  useEffect(() => {
    const handleScroll = () => {
      setState((prev) => ({ ...prev, openNotificationId: null }))
    };
    const scrollableEl = scrollableRef.current;
    if (scrollableEl) {
      scrollableEl.addEventListener("scroll", handleScroll);
      return () => scrollableEl.removeEventListener("scroll", handleScroll);
    }
  }, [state.openNotificationId]);

  const handleOpenDropRight = (itemId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const containerRect = scrollableRef.current?.getBoundingClientRect();
      if (containerRect) {
        setDropdownPosition({
          top: rect.top - containerRect.top + 120,
          left: rect.left + 60,
        });
      }
    }

    setState((prev) => ({
      ...prev,
      openNotificationId: prev.openNotificationId === itemId ? null : itemId, // Toggle the specific notification
    }));
  };
  const updateNotifications = async (
    id: string,
    type: string,
    value: boolean,
  ): Promise<any | null> => {
    try {
      const existingNotification = state.notifications.find(
        (item) => item.id === id
      );

      if (existingNotification && existingNotification[type] === value) {
        return existingNotification; // No update needed, return current item
      }

      if (isAuthenticated) {
        setState((prev) => ({ ...prev, isLoading: true }));
        const res = await UpdateNotifications(
          { notification_id: id, notification_type: type, value: value },
          getToken
        );

        if (res && res.data !== null && res.error === null) {
          const updatedNotifications =
            type === "is_deleted"
              ? state.notifications.filter((item) => item.id !== id)
              : state.notifications.map((item) =>
                item.id === id ? { ...item, [type]: value } : item
              );

          const updatedItem = updatedNotifications.find((item) => item.id === id) || null;

          setState((prev) => ({
            ...prev,
            isLoading: false,
            notifications: updatedNotifications,
          }));

          return updatedItem;
        }
      }
    } catch (error) {
      console.error("Error updating notification:", error);
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    const isDesktop = window.innerWidth >= 768;
    if (
      isDesktop &&
      state.notifications.length > 0 &&
      !state.selectedNotification
    ) {
      const firstNotification = state.notifications[0];
      updateNotifications(firstNotification.id, "is_read", true);
      setState((prev) => ({
        ...prev,
        selectedNotification: firstNotification,
        isList: true,
      }));
    }
    //eslint-disable-next-line
  }, [state.notifications, state.selectedNotification]);

  return (
    <>
      {state.isLoading ? (
        <div className="loader">
          <div className="spinner"></div>
        </div>
      ) : (
        <></>
      )}
      <div className="container message-main sliding-content ps-2">
        <div
          className={`font-500-44 my-3 title d-lg-block ${state.isList ? "d-block" : "d-none"
            }`}
        >
          Alerts
        </div>
        <div
          className={`d-flex d-lg-block ${state.isList ? "d-block" : "d-none"}`}
        >
          <Button
            variant="none"
            className="pill-buttons font-500-18"
            onClick={() => navigate("/message")}
            label={<>Messages</>}
          />
          <Button
            variant="none"
            className="active-pill-buttons ms-3 font-500-18"
            onClick={() => navigate("/alerts")}
            label={<>Alerts</>}
          />
        </div>

        <div
          className={`d-flex align-items-center mt-2 d-lg-none ${state.isList ? "d-none" : "d-block"
            }`}
        >
          <div
            className="icon-circle"
            onClick={() => {
              setState((prev) => ({
                ...prev,
                isList: true,
                selectedNotification: null,
              }));
            }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </div>
        </div>

        {/* If no unread messages*/}
        {Object.keys(state.notifications).length <= 0 ? (
          <div className="mt-3">
            <NullPageComponent
              heading1={"You don't have any alerts yet."}
              heading2={
                "See what's hot in Rankings or browse memberships Available Now!"
              }
              button1={
                <>
                  <Button
                    label="Go to Rankings"
                    className="btn"
                    onClick={() => navigate("/rankings")}
                    variant="dark"
                  />
                  <Button
                    label="Go to Available now!"
                    className="btn"
                    onClick={() => navigate("/available")}
                    variant="dark"
                  />
                </>
              }
            />
          </div>
        ) : (
          <>
            <div className={`sliding-row row mt-4`}>
              {/* Left Panel: Recent Alerts */}
              <div
                className={`col-lg-4 col-12 left-slide-panel left-panel m-0 ${state.isList ? "active" : ""
                  }`}
                style={{ height: "640px" }}
              >
                <div className="relative">
                  <div className="recent-alerts relative p-2 alrtHeight" ref={scrollableRef}>
                    {state.notifications &&
                      state.notifications.length > 0 &&
                      state.notifications.map((item, index) => (
                        <Slab
                          onClick={async () => {
                            const updatedItem = await updateNotifications(item.id, "is_read", true);
                            if (updatedItem) {
                              setState((prev) => ({
                                ...prev,
                                isList: false,
                                selectedNotification: updatedItem,
                              }));
                            }
                          }}
                          key={index}
                          className={`${index === 0 ? "py-2" : "pb-2"} d-flex justify-content-between align-items-center relative ${state.notifications.length > 1 && index === state.notifications.length - 1 ? "for-alert-modal" : ""}`}
                          bgcolor={`bg-midnight p-3 ${item === state.selectedNotification ? "active" : ""}`}
                          content={
                            <div className="d-flex">
                              <div
                                className={`${item.is_read ? "" : "unread-msg"
                                  } my-auto`}
                              ></div>
                              <div className={`p-2 font-500-20 line-height-22 ${item.is_read && "text-extra-light-purple"} ${item === state.selectedNotification && "text-dark-purple"}`}>
                                {parseMessage(item.title, navigate)}
                              </div>
                            </div>
                          }
                          icon={
                            <div className="me-4">
                              <i
                                className="fa-solid fa-ellipsis my-auto mt-0 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDropRight(item.id, e)
                                }}
                              ></i>
                              {/* Show Unfollow button when ellipsis is clicked */}
                              {state.openNotificationId !== null && dropdownPosition && state.openNotificationId === item.id && (
                                <AlertPopupModal
                                  isVisible={
                                    state.openNotificationId === item.id
                                  }
                                  position={dropdownPosition}
                                  OnReadClick={(e) => {
                                    e.stopPropagation();
                                    updateNotifications(
                                      item.id,
                                      "is_read",
                                      true
                                    );
                                    handleOpenDropRight(item.id)
                                  }}
                                  OnUnreadClick={(e) => {
                                    e.stopPropagation();
                                    updateNotifications(
                                      item.id,
                                      "is_read",
                                      false
                                    );
                                    handleOpenDropRight(item.id)
                                  }}
                                  OnDeleteClick={(e) => {
                                    e.stopPropagation();
                                    updateNotifications(
                                      item.id,
                                      "is_deleted",
                                      true
                                    );
                                    handleOpenDropRight(item.id)
                                  }}
                                />
                              )}
                            </div>
                          }
                        />
                      ))}
                  </div>
                </div>
              </div>

              {/* Right Panel: Notification Details */}
              <div
                className={`right-slide-panel col-lg-8 col-12 m-0 ${state.isList === false ? "active" : ""
                  }`}
              >
                {state.selectedNotification ? (
                  <div className="alert-chat">
                    <div className="font-500-26 pb-3">
                      {parseMessage(state.selectedNotification.title, navigate)}
                    </div>
                    <div className="alertLine">
                      {parseMessage(state.selectedNotification.content, navigate)}
                    </div>
                  </div>
                ) : (
                  <div className="alert-chat">
                    <div className="font-500-26 pb-3">
                      Select an alert to view details.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
